import { useCallback, useMemo } from 'react';
import { CampaignType_Enum as CampaignType } from '@weave/schema-gen-ts/dist/schemas/messaging/bulk/v2';
import { useHasBulkEmailAccess, useHasBulkTextAccess } from '@frontend/feature-access';
import { useOrgData } from '@frontend/get-org-data';
import { useAppScopeStore } from '@frontend/scope';
import { BulkMessageEditorProvider } from '../bulk-message-editor-provider';
import { BulkMessageEditorPage } from '../components/bulk-message-editor-page';
import { EmptyStateNoAccess } from '../components/empty-state-no-access';
import { useCampaignSave } from '../hooks/use-campaign-save';
import { EditorView } from '../types';
import { sortItems } from '../utils/sort-items';

type Props = {
  campaignType: CampaignType;
  view: EditorView;
};

export const CampaignCreate = ({ campaignType, view }: Props) => {
  const { lastSavedCampaign, ...editorActions } = useCampaignSave(campaignType);

  const { hasBulkEmailAccess, locationsWithBulkEmailAccess, locationsWithBulkEmailEnabled } = useHasBulkEmailAccess();
  const { hasBulkTextAccess, locationsWithBulkTextAccess, locationsWithBulkTextEnabled } = useHasBulkTextAccess();
  const hasAccess = campaignType === CampaignType.EMAIL ? hasBulkEmailAccess : hasBulkTextAccess;
  const locationsWithACLAccess =
    campaignType === CampaignType.EMAIL ? locationsWithBulkEmailAccess : locationsWithBulkTextAccess;
  const locationsWithEnabledFlag =
    campaignType === CampaignType.EMAIL ? locationsWithBulkEmailEnabled : locationsWithBulkTextEnabled;

  // get list of location ids with feature access from the globally selected location ids
  const { selectedLocationIds: appSelectedLocationIds } = useAppScopeStore();
  const { getLocationIdsWithoutParent } = useOrgData();
  const getFilteredLocationIdsWithAccess = useCallback(
    (newLocationIds: string[]) => {
      const enabledLocationIdsWithAccess = locationsWithACLAccess.filter((id) =>
        locationsWithEnabledFlag.find((locationId) => locationId === id)
      );
      return newLocationIds.filter((id) => enabledLocationIdsWithAccess.includes(id));
    },
    [locationsWithACLAccess, locationsWithEnabledFlag]
  );
  const sortedLocationIdsWithoutParent = useMemo(
    () => sortItems(getFilteredLocationIdsWithAccess(getLocationIdsWithoutParent(appSelectedLocationIds))),
    [appSelectedLocationIds, getFilteredLocationIdsWithAccess, getLocationIdsWithoutParent]
  );

  if (!hasAccess) {
    return <EmptyStateNoAccess campaignType={campaignType} />;
  }

  return (
    <BulkMessageEditorProvider
      {...editorActions}
      campaignType={campaignType}
      initialCampaign={lastSavedCampaign}
      initialLocationIds={sortedLocationIdsWithoutParent}
      view={view}
    >
      <BulkMessageEditorPage />
    </BulkMessageEditorProvider>
  );
};
